import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
  type RefObject,
} from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ImagePlus, X } from 'lucide-react'
import type { Photo, PublicWedding } from '@shared/types'
import { ApiError, api, mensajeDeError } from '../../lib/api'
import { Button, ErrorMessage, Field, Spinner, TextArea } from '../../ui'
import { CordMark, CordSpine } from '../../ui/Cord'
import { BOTON_CLARO, BOTON_CONTORNO, cx } from './secciones/comunes'

/* ===========================================================================
   Subir fotos — la página del código QR de las mesas.

   Un invitado con el teléfono en una mano y la copa en la otra: toca el área
   grande, el navegador le ofrece cámara o galería, escribe su nombre una sola
   vez (queda guardado) y envía. Sin cuenta y sin instalar nada.

   El cordón vuelve al borde izquierdo y se traza con el avance: elegir,
   revisar, enviar.
   =========================================================================== */

const RIEL = 'w-[18px] sm:w-7 lg:w-9'
const RIEL_HUECO = 'pl-[18px] sm:pl-7 lg:pl-9'

/** Los mismos topes que aplica el Worker, dichos antes de que fallen. */
const MAX_ARCHIVOS = 10
const MAX_BYTES = 10 * 1024 * 1024

/** El nombre se recuerda por boda: nadie debe escribirlo dos veces. */
function claveDeNombre(slug: string): string {
  return `nuestra-boda.subir.${slug}`
}

function leerNombre(slug: string): string {
  try {
    return localStorage.getItem(claveDeNombre(slug)) ?? ''
  } catch {
    return ''
  }
}

function guardarNombre(slug: string, nombre: string): void {
  try {
    const limpio = nombre.trim()
    if (limpio === '') localStorage.removeItem(claveDeNombre(slug))
    else localStorage.setItem(claveDeNombre(slug), limpio)
  } catch {
    // Almacenamiento bloqueado: el invitado escribe el nombre esta vez y ya.
  }
}

function pesoLegible(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  if (mb >= 1) return `${new Intl.NumberFormat('es-EC', { maximumFractionDigits: 1 }).format(mb)} MB`
  const kb = Math.max(1, Math.round(bytes / 1024))
  return `${kb} KB`
}

/** jsdom (y algún navegador antiguo) no tiene createObjectURL: sin vista previa, pero sube igual. */
function crearVistaPrevia(file: File): string {
  try {
    return URL.createObjectURL(file)
  } catch {
    return ''
  }
}

function soltarVistaPrevia(url: string): void {
  if (url === '') return
  try {
    URL.revokeObjectURL(url)
  } catch {
    // Nada que soltar.
  }
}

interface Elegida {
  id: string
  file: File
  url: string
  /** Motivo por el que esta foto no puede ir. null si está bien. */
  problema: string | null
}

function revisar(file: File): string | null {
  if (file.type !== '' && !file.type.startsWith('image/')) return 'No es una imagen.'
  if (file.size > MAX_BYTES) return `Pesa ${pesoLegible(file.size)} y el máximo es 10 MB.`
  if (file.size === 0) return 'El archivo llegó vacío.'
  return null
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function SubirFotos() {
  const { slug = '' } = useParams()

  const [boda, setBoda] = useState<PublicWedding | null>(null)
  const [cargando, setCargando] = useState(true)
  const [fallo, setFallo] = useState<{ noEncontrada: boolean; mensaje: string } | null>(null)
  const [intento, setIntento] = useState(0)
  /** Las subidas se pueden cerrar mientras el invitado está en la página. */
  const [cerrado, setCerrado] = useState(false)

  const [elegidas, setElegidas] = useState<Elegida[]>([])
  const [nombre, setNombre] = useState(() => leerNombre(slug))
  const [descripcion, setDescripcion] = useState('')
  const [subiendo, setSubiendo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ultimaTanda, setUltimaTanda] = useState<number | null>(null)
  const [total, setTotal] = useState(0)

  const entrada = useRef<HTMLInputElement>(null)
  const tituloRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    let vigente = true
    setCargando(true)
    setFallo(null)

    api
      .get<{ wedding: PublicWedding }>(`/api/public/${encodeURIComponent(slug)}`)
      .then(({ wedding }) => {
        if (vigente) setBoda(wedding)
      })
      .catch((problema: unknown) => {
        if (!vigente) return
        setBoda(null)
        setFallo({
          noEncontrada: problema instanceof ApiError && problema.esNoEncontrado,
          mensaje: mensajeDeError(problema),
        })
      })
      .finally(() => {
        if (vigente) setCargando(false)
      })

    return () => {
      vigente = false
    }
  }, [slug, intento])

  useEffect(() => {
    setNombre(leerNombre(slug))
  }, [slug])

  // El título de la pantalla de "listo" recibe el foco tras cada tanda.
  useEffect(() => {
    if (ultimaTanda === null) return
    tituloRef.current?.focus()
  }, [ultimaTanda])

  useEffect(() => {
    if (!boda) return
    const anterior = document.title
    document.title = `Subir fotos · ${boda.partner1Name} y ${boda.partner2Name}`
    return () => {
      document.title = anterior
    }
  }, [boda])

  // Las URL de vista previa son memoria del navegador: se sueltan al salir.
  const vigentes = useRef<Elegida[]>([])
  vigentes.current = elegidas

  useEffect(() => {
    return () => {
      for (const elegida of vigentes.current) soltarVistaPrevia(elegida.url)
    }
  }, [])

  // Mientras sube, el navegador pregunta antes de cerrar: perder la tanda a
  // medio camino es la peor manera de terminar esta página.
  useEffect(() => {
    if (!subiendo) return
    const avisar = (evento: BeforeUnloadEvent) => {
      evento.preventDefault()
      evento.returnValue = ''
    }
    window.addEventListener('beforeunload', avisar)
    return () => window.removeEventListener('beforeunload', avisar)
  }, [subiendo])

  function reemplazarSeleccion(nuevas: Elegida[]) {
    setElegidas((previas) => {
      for (const previa of previas) soltarVistaPrevia(previa.url)
      return nuevas
    })
  }

  function alElegir(evento: ChangeEvent<HTMLInputElement>) {
    const lista = evento.target.files
    const archivos = lista === null ? [] : Array.from(lista)

    setError(null)
    reemplazarSeleccion(
      archivos.map((file, i) => ({
        id: `${Date.now()}-${i}-${file.name}`,
        file,
        url: crearVistaPrevia(file),
        problema: revisar(file),
      })),
    )

    // Se vacía el input para que volver a elegir los mismos archivos siga
    // disparando el evento. Los File ya están copiados en el estado.
    evento.target.value = ''
  }

  function quitar(id: string) {
    setElegidas((previas) => {
      const fuera = previas.find((elegida) => elegida.id === id)
      if (fuera) soltarVistaPrevia(fuera.url)
      return previas.filter((elegida) => elegida.id !== id)
    })
    setError(null)
  }

  function limpiarTodo() {
    reemplazarSeleccion([])
    if (entrada.current) entrada.current.value = ''
  }

  const sobran = elegidas.length - MAX_ARCHIVOS
  const conProblema = elegidas.filter((elegida) => elegida.problema !== null).length
  const puedeSubir = elegidas.length > 0 && sobran <= 0 && conProblema === 0 && !subiendo

  async function subir() {
    if (!puedeSubir) return

    const formulario = new FormData()
    for (const elegida of elegidas) formulario.append('archivos', elegida.file)
    const quien = nombre.trim()
    const pie = descripcion.trim()
    if (quien !== '') formulario.append('uploaderName', quien)
    if (pie !== '') formulario.append('caption', pie)

    setSubiendo(true)
    setError(null)

    try {
      const { photos } = await api.upload<{ photos: Photo[] }>(
        `/api/public/${encodeURIComponent(slug)}/fotos`,
        formulario,
      )
      guardarNombre(slug, quien)
      limpiarTodo()
      setDescripcion('')
      setUltimaTanda(photos.length)
      setTotal((previo) => previo + photos.length)
    } catch (problema: unknown) {
      setError(mensajeDeSubida(problema, () => setCerrado(true)))
    } finally {
      setSubiendo(false)
    }
  }

  if (cargando) return <Cargando />
  if (fallo?.noEncontrada) return <SinBoda />
  if (fallo) return <FalloDeCarga mensaje={fallo.mensaje} onReintentar={() => setIntento((n) => n + 1)} />
  if (!boda) return <SinBoda />

  const permite = boda.settings.rsvp.allowGuestUploads && !cerrado
  const avance = ultimaTanda !== null ? 1 : elegidas.length > 0 ? 0.67 : 0.34

  return (
    <div className="relative min-h-dvh bg-bruma">
      <div
        aria-hidden="true"
        className={cx('fixed inset-y-0 left-0 z-20 bg-tinta text-laton-claro', RIEL)}
      >
        <CordSpine progress={avance} knots={[0.34, 0.67, 1]} className="h-full w-full" />
      </div>

      <main className={cx('min-h-dvh', RIEL_HUECO)}>
        <Encabezado boda={boda} />

        <div className="px-5 py-8 sm:px-10 sm:py-10 lg:px-16">
          <div className="mx-auto w-full max-w-xl">
            {!permite ? (
              <Cerradas slug={boda.slug} />
            ) : (
              <>
                {ultimaTanda !== null && (
                  <Recibidas
                    tituloRef={tituloRef}
                    cuantas={ultimaTanda}
                    total={total}
                    modera={boda.settings.rsvp.moderatePhotos}
                    slug={boda.slug}
                  />
                )}

                <div className={ultimaTanda !== null ? 'mt-8' : ''}>
                  {ultimaTanda === null && (
                    <>
                      <p className="eyebrow">Fotos</p>
                      <h2
                        ref={tituloRef}
                        tabIndex={-1}
                        className="mt-3 font-display text-titulo text-tinta"
                      >
                        Comparte lo que viste
                      </h2>
                      <p className="mt-4 leading-relaxed text-tinta/85">
                        Tus fotos se suman a la galería de la invitación
                        {boda.settings.rsvp.moderatePhotos
                          ? ', en cuanto los novios las revisen.'
                          : '.'}{' '}
                        No necesitas cuenta ni aplicación.
                      </p>
                    </>
                  )}

                  <Selector
                    entrada={entrada}
                    elegidas={elegidas}
                    onElegir={alElegir}
                    onQuitar={quitar}
                    onLimpiar={limpiarTodo}
                    subiendo={subiendo}
                    className={ultimaTanda === null ? 'mt-8' : 'mt-6'}
                  />

                  {/* Sin aria-live propio: cada aviso ya es role="alert". */}
                  <div className="mt-4 flex flex-col gap-3">
                    {sobran > 0 && (
                      <ErrorMessage>
                        {`Elegiste ${elegidas.length} fotos y podemos recibir ${MAX_ARCHIVOS} por vez. Quita ${sobran} y súbelas en una segunda tanda.`}
                      </ErrorMessage>
                    )}
                    {conProblema > 0 && (
                      <ErrorMessage>
                        {conProblema === 1
                          ? 'Una de las fotos no se puede enviar. Quítala para continuar.'
                          : `${conProblema} fotos no se pueden enviar. Quítalas para continuar.`}
                      </ErrorMessage>
                    )}
                    {error !== null && <ErrorMessage>{error}</ErrorMessage>}
                  </div>

                  <div className="mt-6 flex flex-col gap-4">
                    <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2 sm:gap-4">
                      <Field
                        label="¿Cómo te llamas?"
                        value={nombre}
                        onChange={(evento) => setNombre(evento.target.value)}
                        onBlur={() => guardarNombre(slug, nombre)}
                        hint="Para que los novios sepan de quién es cada foto. Lo guardamos en este teléfono."
                        autoComplete="name"
                        autoCapitalize="words"
                        maxLength={80}
                        className="min-h-[52px] text-base"
                      />

                      <TextArea
                        label="¿Qué es esta foto? (opcional)"
                        value={descripcion}
                        onChange={(evento) => setDescripcion(evento.target.value)}
                        hint="Una línea para toda la tanda. Por ejemplo: “el brindis de la mesa 4”."
                        maxLength={300}
                        rows={2}
                        className="text-base"
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={() => void subir()}
                      loading={subiendo}
                      disabled={!puedeSubir}
                      full
                      className="min-h-[52px] text-base"
                    >
                      {subiendo
                        ? 'Subiendo…'
                        : elegidas.length === 0
                          ? 'Elige tus fotos para continuar'
                          : elegidas.length === 1
                            ? 'Subir mi foto'
                            : `Subir mis ${elegidas.length} fotos`}
                    </Button>

                    {/* Región siempre montada para que el aviso de subida se anuncie. */}
                    <p aria-live="polite" className="text-sm text-palo-hondo">
                      {subiendo
                        ? 'Estamos subiendo tus fotos. No cierres esta página hasta que terminemos.'
                        : elegidas.length > 0
                          ? 'Con datos móviles puede tardar un poco: quédate en la página mientras suben.'
                          : ''}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

/** Traduce los fallos frecuentes a algo que un invitado pueda resolver. */
function mensajeDeSubida(problema: unknown, alCerrarse: () => void): string {
  if (problema instanceof ApiError) {
    if (problema.esLimiteDePlan) {
      return 'Esta boda llegó al máximo de fotos que permite su plan. Avísales a los novios: en cuanto lo amplíen, vuelves a intentarlo.'
    }
    if (problema.status === 413) {
      return 'Alguna foto pesa más de 10 MB. Quítala o vuelve a tomarla con menos calidad y súbela otra vez.'
    }
    if (problema.status === 403) {
      alCerrarse()
      return problema.message
    }
    return problema.message
  }
  return mensajeDeError(problema)
}

// ---------------------------------------------------------------------------
// Encabezado
// ---------------------------------------------------------------------------

function Encabezado({ boda }: { boda: PublicWedding }) {
  return (
    <header className="bg-tinta px-5 pb-8 pt-6 sm:px-10 sm:pb-10 sm:pt-8 lg:px-16">
      {/* Mismo eje que la columna de contenido de abajo. */}
      <div className="mx-auto w-full max-w-xl">
        <Link
          to={`/b/${boda.slug}`}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm text-palo transition-colors hover:text-bruma"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          Volver a la invitación
        </Link>

        <p className="eyebrow mt-6 text-laton-claro">Galería de todos</p>
        <h1 className="mt-3 font-display text-titulo text-bruma">
          {boda.partner1Name} y {boda.partner2Name}
        </h1>
      </div>
    </header>
  )
}

// ---------------------------------------------------------------------------
// Selector de archivos
// ---------------------------------------------------------------------------

interface SelectorProps {
  entrada: RefObject<HTMLInputElement | null>
  elegidas: Elegida[]
  onElegir: (evento: ChangeEvent<HTMLInputElement>) => void
  onQuitar: (id: string) => void
  onLimpiar: () => void
  subiendo: boolean
  className?: string
}

function Selector({
  entrada,
  elegidas,
  onElegir,
  onQuitar,
  onLimpiar,
  subiendo,
  className,
}: SelectorProps) {
  const vacio = elegidas.length === 0

  return (
    <div className={className}>
      <input
        ref={entrada}
        id="archivos"
        type="file"
        accept="image/*"
        multiple
        disabled={subiendo}
        onChange={onElegir}
        className="peer sr-only"
      />
      <label
        htmlFor="archivos"
        className={cx(
          'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border border-dashed',
          'border-palo bg-lienzo px-6 text-center transition-colors hover:bg-palo/25',
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
          'peer-focus-visible:outline-laton peer-disabled:cursor-not-allowed peer-disabled:opacity-60',
          vacio ? 'min-h-[176px] py-10' : 'min-h-[88px] py-6',
        )}
      >
        <ImagePlus size={18} aria-hidden="true" className="text-palo-hondo" />
        <span className="font-display text-xl text-tinta">
          {vacio ? 'Toca para elegir tus fotos' : 'Elegir otras fotos'}
        </span>
        {vacio && (
          <span className="max-w-xs text-sm leading-relaxed text-palo-hondo">
            Tu teléfono te ofrece la cámara o la galería. Hasta {MAX_ARCHIVOS} fotos por vez.
          </span>
        )}
      </label>

      {!vacio && (
        <>
          <div className="mt-5 flex items-baseline justify-between gap-4">
            <p className="eyebrow">
              {elegidas.length === 1 ? 'Una foto elegida' : `${elegidas.length} fotos elegidas`}
            </p>
            <button
              type="button"
              onClick={onLimpiar}
              disabled={subiendo}
              className="text-sm text-palo-hondo underline underline-offset-4 transition-colors hover:text-tinta disabled:opacity-50"
            >
              Quitar todas
            </button>
          </div>

          <ul className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {elegidas.map((elegida) => (
              <li key={elegida.id} className="relative">
                <div
                  className={cx(
                    'aspect-square w-full overflow-hidden rounded-md bg-palo/40',
                    elegida.problema !== null && 'ring-2 ring-ladrillo',
                  )}
                >
                  {elegida.url === '' ? (
                    <span className="flex h-full w-full items-center justify-center px-1 text-center text-[0.625rem] leading-tight text-palo-hondo">
                      {elegida.file.name}
                    </span>
                  ) : (
                    <img
                      src={elegida.url}
                      alt={`Foto elegida: ${elegida.file.name}`}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => onQuitar(elegida.id)}
                  disabled={subiendo}
                  aria-label={`Quitar ${elegida.file.name}`}
                  className="absolute right-1 top-1 flex size-8 items-center justify-center rounded-full bg-tinta/85 text-bruma transition-colors hover:bg-tinta disabled:opacity-50"
                >
                  <X size={14} aria-hidden="true" />
                </button>

                {elegida.problema !== null && (
                  <p className="mt-1 text-[0.6875rem] leading-tight text-ladrillo">
                    {elegida.problema}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Resultado
// ---------------------------------------------------------------------------

interface RecibidasProps {
  tituloRef: RefObject<HTMLHeadingElement | null>
  cuantas: number
  total: number
  modera: boolean
  slug: string
}

function Recibidas({ tituloRef, cuantas, total, modera, slug }: RecibidasProps) {
  return (
    <div>
      <CordMark className="h-4 w-10 text-laton" />
      <p className="eyebrow mt-6">Listo</p>
      <h2 ref={tituloRef} tabIndex={-1} className="mt-3 font-display text-titulo text-tinta">
        {cuantas === 1 ? 'Subiste tu foto' : `Subiste ${cuantas} fotos`}
      </h2>
      <p className="mt-4 leading-relaxed text-tinta/85">
        {modera
          ? 'Aparecerán en la galería de la invitación en cuanto los novios las aprueben.'
          : 'Ya están en la galería de la invitación.'}
      </p>
      {total > cuantas && (
        <p className="mt-2 text-sm text-palo-hondo">
          Con esta tanda llevas <span className="tabular font-mono">{total}</span> fotos compartidas.
          Gracias.
        </p>
      )}

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link to={`/b/${slug}#galeria`} className={BOTON_CLARO}>
          Ver la galería
        </Link>
        <Link to={`/b/${slug}`} className={BOTON_CONTORNO}>
          Volver a la invitación
        </Link>
      </div>

      <hr className="regla mt-8" />
      <p className="mt-4 font-display text-xl text-tinta">¿Tienes más?</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Estados de página completa
// ---------------------------------------------------------------------------

function Cerradas({ slug }: { slug: string }) {
  return (
    <>
      <p className="eyebrow">Fotos</p>
      <h2 className="mt-3 font-display text-titulo text-tinta">Las subidas están cerradas</h2>
      <p className="mt-4 leading-relaxed text-tinta/85">
        Los novios desactivaron las fotos de invitados en esta boda, así que por ahora no podemos
        recibir la tuya. Si crees que es un error, escríbeles: pueden volver a abrirlas cuando
        quieran.
      </p>
      <Link to={`/b/${slug}`} className={cx(BOTON_CLARO, 'mt-8')}>
        Volver a la invitación
      </Link>
    </>
  )
}

function Cargando() {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-tinta px-6">
      <div className="flex flex-col items-start gap-4 text-palo">
        <CordMark className="h-4 w-10 text-laton-claro" />
        <p className="flex items-center gap-3 text-sm">
          <Spinner small />
          Abriendo la galería…
        </p>
      </div>
    </main>
  )
}

function Envoltura({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center bg-bruma px-6 py-16 sm:px-12">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </main>
  )
}

function SinBoda() {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No encontramos esa boda</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">
        El enlace puede tener una letra de más, o los novios todavía no publicaron su página. Si lo
        escaneaste de un código QR, vuelve a intentarlo con más luz y la cámara quieta.
      </p>
      <Link to="/" className={cx(BOTON_CONTORNO, 'mt-8')}>
        Conocer Nuestra Boda
      </Link>
    </Envoltura>
  )
}

function FalloDeCarga({ mensaje, onReintentar }: { mensaje: string; onReintentar: () => void }) {
  return (
    <Envoltura>
      <CordMark className="h-4 w-10 text-laton" />
      <h1 className="mt-6 font-display text-titulo text-tinta">No pudimos abrir esta página</h1>
      <p className="mt-4 leading-relaxed text-palo-hondo">{mensaje}</p>
      <button type="button" onClick={onReintentar} className={cx(BOTON_CLARO, 'mt-8')}>
        Intentar de nuevo
      </button>
    </Envoltura>
  )
}
